import type { ReactNode } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DraggableAttributes,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useT } from "@reciperoll/core";

export type DragHandle = {
  ref: (el: HTMLElement | null) => void;
  attributes: DraggableAttributes;
  listeners: Record<string, unknown> | undefined;
};

// Vertical drag-to-reorder list. `renderItem` gets a `handle` to spread onto a
// drag-handle element so only the handle starts a drag (inputs stay editable).
// Pointer + keyboard sensors → works with mouse, touch, and keyboard.
export function SortableList<T extends { id: string }>({
  items,
  onReorder,
  className = "",
  renderItem,
}: {
  items: T[];
  onReorder: (items: T[]) => void;
  className?: string;
  renderItem: (item: T, handle: DragHandle) => ReactNode;
}) {
  const sensors = useSensors(
    // Small distance so a click/tap on the handle doesn't accidentally drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = items.findIndex((i) => i.id === active.id);
    const to = items.findIndex((i) => i.id === over.id);
    if (from !== -1 && to !== -1) onReorder(arrayMove(items, from, to));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        <div className={className}>
          {items.map((item) => (
            <SortableRow key={item.id} id={item.id}>
              {(handle) => renderItem(item, handle)}
            </SortableRow>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({
  id,
  children,
}: {
  id: string;
  children: (handle: DragHandle) => ReactNode;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : undefined,
    position: "relative",
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ ref: setActivatorNodeRef, attributes, listeners })}
    </div>
  );
}

// Grip drag-handle (react-feather has no grip icon). `touch-none` so touch drags
// don't scroll the page instead of reordering.
export function GripHandle({ handle, className = "" }: { handle: DragHandle; className?: string }) {
  const t = useT();
  return (
    <button
      type="button"
      ref={handle.ref}
      {...handle.attributes}
      {...handle.listeners}
      aria-label={t("common.dragToReorder")}
      className={`shrink-0 flex items-center justify-center text-ink-faint hover:text-ink-soft cursor-grab active:cursor-grabbing touch-none ${className}`}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <circle cx="5.5" cy="4" r="1.3" />
        <circle cx="10.5" cy="4" r="1.3" />
        <circle cx="5.5" cy="8" r="1.3" />
        <circle cx="10.5" cy="8" r="1.3" />
        <circle cx="5.5" cy="12" r="1.3" />
        <circle cx="10.5" cy="12" r="1.3" />
      </svg>
    </button>
  );
}
